import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModal } from '../../../contexts/ModalContext';
import { useUser } from '../../../contexts/UserContext';
import HelpInvitation from '../components/HelpInvitation';
import HelpTour from '../components/HelpTour';
import HelpWaitingPrompt from '../components/HelpWaitingPrompt';
import { HELP_CONFIG, isOrientationEnabledForUser } from '../config/helpConfig';
import {
    getOrientationStepIndex,
    findOrientationInteraction,
    ORIENTATION_TOUR,
    ORIENTATION_VERSION,
} from '../content/orientationTour';
import {
    isInvitationSuppressed,
    readOrientationRecord,
    suppressInvitation,
    writeOrientationRecord,
} from '../services/helpStorage';
import { watchForHelpTarget } from '../services/targetResolver';
import { HELP_ANCHOR_ATTRIBUTE } from '../config/helpAnchors';
import {
    INITIAL_ORIENTATION_STATE,
    orientationReducer,
    ORIENTATION_STATUSES,
} from './orientationState';
import { HelpContext } from './useHelp';

const INVITATION_ROUTES = Object.freeze(['/dashboard', '/new-report']);
const TERMINAL_ORIENTATION_STATUSES = Object.freeze([
    ORIENTATION_STATUSES.COMPLETED,
    ORIENTATION_STATUSES.DISMISSED,
]);
const defaultNow = () => new Date();

const getUserId = (user) => user?._id || user?.id || null;

const hasBlockingDocumentDialog = (documentObject = document) => (
    [...documentObject.querySelectorAll('[role="dialog"], [aria-modal="true"]')]
        .some((dialog) => !dialog.closest('[data-help-tour-root]'))
);

const createMemoryRecord = ({ status, lastStepId, checkpointId = null, explicitRestart = false, now }) => ({
    version: ORIENTATION_VERSION,
    status,
    lastStepId,
    checkpointId,
    ...(explicitRestart ? { explicitRestart: true } : {}),
    updatedAt: now().toISOString(),
});

const HelpProvider = ({
    children,
    config = HELP_CONFIG,
    storage,
    sessionStorage,
    now = defaultNow,
    targetResolver = watchForHelpTarget,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const userContext = useUser();
    const modalContext = useModal();
    const user = userContext?.state?.user;
    const userId = getUserId(user);
    const accountTutorialCompleted = user?.settings?.helpTutorialCompleted === true;
    const canUseOrientation = isOrientationEnabledForUser(user, config);
    const localStorageObject = storage ?? (typeof window !== 'undefined' ? window.localStorage : null);
    const sessionStorageObject = sessionStorage ?? (typeof window !== 'undefined' ? window.sessionStorage : null);
    const [state, dispatch] = useReducer(orientationReducer, INITIAL_ORIENTATION_STATE);
    const [record, setRecord] = useState(null);
    const initializedUserRef = useRef(null);
    const initializedAccountCompletionRef = useRef(null);
    const memoryRecordRef = useRef(null);
    const memoryInvitationSuppressedRef = useRef(false);
    const explicitRestartRef = useRef(false);
    const accountCompletionSyncRef = useRef(false);
    const returnFocusRef = useRef(null);
    const expectedRouteRef = useRef(null);
    const currentStep = ORIENTATION_TOUR[state.currentStepIndex] || ORIENTATION_TOUR[0];
    const blockingModal = Boolean(
        modalContext?.state?.confirmModal?.isOpen
        || modalContext?.state?.selectedOptions?.isShowing
    );

    const persist = useCallback((status, lastStepId, checkpointId = null) => {
        const explicitRestart = explicitRestartRef.current
            && [ORIENTATION_STATUSES.PAUSED, ORIENTATION_STATUSES.WAITING].includes(status);
        const persisted = writeOrientationRecord({
            storage: localStorageObject,
            userId,
            status,
            lastStepId,
            checkpointId,
            explicitRestart,
            now,
        });
        const nextRecord = persisted || createMemoryRecord({
            status,
            lastStepId,
            checkpointId,
            explicitRestart,
            now,
        });
        memoryRecordRef.current = nextRecord;
        setRecord(nextRecord);
        return nextRecord;
    }, [localStorageObject, now, userId]);

    const markTutorialCompletedForAccount = useCallback(() => {
        if (
            accountTutorialCompleted
            || accountCompletionSyncRef.current
            || typeof userContext?.updateUserSettings !== 'function'
        ) return;

        accountCompletionSyncRef.current = true;
        Promise.resolve()
            .then(() => userContext.updateUserSettings({ helpTutorialCompleted: true }))
            .catch(() => undefined)
            .finally(() => {
                accountCompletionSyncRef.current = false;
            });
    }, [accountTutorialCompleted, userContext]);

    useEffect(() => {
        accountCompletionSyncRef.current = false;
    }, [userId]);

    useEffect(() => {
        if (!canUseOrientation || !userId) {
            initializedUserRef.current = null;
            initializedAccountCompletionRef.current = null;
            memoryRecordRef.current = null;
            memoryInvitationSuppressedRef.current = false;
            explicitRestartRef.current = false;
            setRecord(null);
            dispatch({ type: 'RESET' });
            return;
        }

        if (
            initializedUserRef.current === userId
            && initializedAccountCompletionRef.current === accountTutorialCompleted
        ) return;

        initializedUserRef.current = userId;
        initializedAccountCompletionRef.current = accountTutorialCompleted;
        const storedRecord = readOrientationRecord({ storage: localStorageObject, userId });
        memoryRecordRef.current = storedRecord;
        explicitRestartRef.current = storedRecord?.explicitRestart === true;
        memoryInvitationSuppressedRef.current = isInvitationSuppressed({
            sessionStorage: sessionStorageObject,
            userId,
        });
        setRecord(storedRecord);
        if (
            storedRecord?.version === ORIENTATION_VERSION
            && storedRecord.status === ORIENTATION_STATUSES.WAITING
            && (!accountTutorialCompleted || storedRecord.explicitRestart === true)
        ) {
            const interaction = findOrientationInteraction({
                checkpointId: storedRecord.checkpointId ?? null,
                resumeStepId: storedRecord.lastStepId,
            });
            if (interaction) {
                dispatch({
                    type: 'RESTORE_WAITING',
                    payload: {
                        stepIndex: getOrientationStepIndex(interaction.resumeStepId),
                        waiting: { ...interaction, isRestoring: true },
                    },
                });
                return;
            }
        }
        dispatch({ type: 'RESET' });
    }, [accountTutorialCompleted, canUseOrientation, localStorageObject, sessionStorageObject, userId]);

    useEffect(() => {
        if (record && TERMINAL_ORIENTATION_STATUSES.includes(record.status)) {
            markTutorialCompletedForAccount();
        }
    }, [markTutorialCompletedForAccount, record]);

    useEffect(() => {
        if (
            !canUseOrientation
            || initializedUserRef.current !== userId
            || !INVITATION_ROUTES.includes(location.pathname)
            || ![ORIENTATION_STATUSES.IDLE, ORIENTATION_STATUSES.PAUSED].includes(state.status)
            || memoryInvitationSuppressedRef.current
        ) return;

        const currentRecord = memoryRecordRef.current || record;
        const hasCurrentLocalResume = currentRecord?.version === ORIENTATION_VERSION
            && currentRecord.status === ORIENTATION_STATUSES.PAUSED
            && (!accountTutorialCompleted || currentRecord.explicitRestart === true);
        if (
            (accountTutorialCompleted || TERMINAL_ORIENTATION_STATUSES.includes(currentRecord?.status))
            && !hasCurrentLocalResume
        ) return;

        const hasOlderVersion = Number.isInteger(currentRecord?.version)
            && currentRecord.version < ORIENTATION_VERSION;
        const kind = state.status === ORIENTATION_STATUSES.PAUSED
            ? 'resume'
            : hasOlderVersion
            ? 'updated'
            : currentRecord?.version === ORIENTATION_VERSION && currentRecord.status === ORIENTATION_STATUSES.PAUSED
                ? 'resume'
                : 'start';
        const stepIndex = kind === 'resume'
            ? getOrientationStepIndex(currentRecord.lastStepId)
            : 0;

        dispatch({ type: 'INVITE', payload: { kind, stepIndex } });
    }, [accountTutorialCompleted, canUseOrientation, location.pathname, record, state.status, userId]);

    const moveToRouteForStep = useCallback((step) => {
        if (step.routes.includes(location.pathname)) return;
        expectedRouteRef.current = step.preferredRoute;
        navigate(step.preferredRoute);
    }, [location.pathname, navigate]);

    const startAtStep = useCallback((stepIndex, trigger) => {
        if (!canUseOrientation || blockingModal || hasBlockingDocumentDialog()) return;

        const nextStep = ORIENTATION_TOUR[stepIndex] || ORIENTATION_TOUR[0];
        returnFocusRef.current = trigger || document.activeElement;
        moveToRouteForStep(nextStep);
        persist(ORIENTATION_STATUSES.PAUSED, nextStep.id, null);
        dispatch({ type: 'START', payload: { stepIndex } });
    }, [blockingModal, canUseOrientation, moveToRouteForStep, persist]);

    const restoreWaiting = useCallback((interaction, trigger) => {
        if (!canUseOrientation || !interaction) return;

        const stepIndex = getOrientationStepIndex(interaction.resumeStepId);
        const nextStep = ORIENTATION_TOUR[stepIndex] || ORIENTATION_TOUR[0];
        returnFocusRef.current = trigger || document.activeElement;
        moveToRouteForStep(nextStep);
        persist(ORIENTATION_STATUSES.WAITING, nextStep.id, interaction.checkpointId);
        dispatch({
            type: 'RESTORE_WAITING',
            payload: { stepIndex, waiting: { ...interaction, isRestoring: true } },
        });
    }, [canUseOrientation, moveToRouteForStep, persist]);

    const startOrientation = useCallback((trigger) => {
        explicitRestartRef.current = false;
        startAtStep(state.invitationKind === 'resume' ? state.currentStepIndex : 0, trigger);
    }, [startAtStep, state.currentStepIndex, state.invitationKind]);

    const resumeOrientation = useCallback((trigger) => {
        const currentRecord = memoryRecordRef.current || record;
        explicitRestartRef.current = currentRecord?.explicitRestart === true;
        if (currentRecord?.checkpointId !== null && currentRecord?.checkpointId !== undefined) {
            const interaction = findOrientationInteraction({
                checkpointId: currentRecord.checkpointId,
                resumeStepId: currentRecord.lastStepId,
            });
            if (interaction) {
                restoreWaiting(interaction, trigger);
                return;
            }
        }
        startAtStep(getOrientationStepIndex(currentRecord?.lastStepId), trigger);
    }, [record, restoreWaiting, startAtStep]);

    const restartOrientation = useCallback((trigger) => {
        explicitRestartRef.current = true;
        startAtStep(0, trigger);
    }, [startAtStep]);

    const pauseOrientation = useCallback(() => {
        if (state.status !== ORIENTATION_STATUSES.RUNNING) return;
        persist(ORIENTATION_STATUSES.PAUSED, currentStep.id, null);
        dispatch({ type: 'PAUSE' });
    }, [currentStep.id, persist, state.status]);

    const beginOrientationInteraction = useCallback((interactionType) => {
        if (state.status !== ORIENTATION_STATUSES.RUNNING) return;

        const interaction = currentStep[interactionType];
        if (!interaction) return;

        const stepIndex = getOrientationStepIndex(interaction.resumeStepId);
        persist(ORIENTATION_STATUSES.WAITING, interaction.resumeStepId, interaction.checkpointId);
        dispatch({
            type: 'WAIT',
            payload: { stepIndex, waiting: interaction },
        });
    }, [currentStep, persist, state.status]);

    const continueWaitingOrientation = useCallback(() => {
        if (state.status !== ORIENTATION_STATUSES.WAITING) return;
        persist(ORIENTATION_STATUSES.PAUSED, currentStep.id, null);
        dispatch({ type: 'CONTINUE_WAITING' });
    }, [currentStep.id, persist, state.status]);

    const pauseWaitingOrientation = useCallback(() => {
        if (state.status !== ORIENTATION_STATUSES.WAITING) return;
        persist(ORIENTATION_STATUSES.PAUSED, currentStep.id, state.waiting?.checkpointId ?? null);
        dispatch({ type: 'PAUSE' });
    }, [currentStep.id, persist, state.status, state.waiting]);

    const dismissOrientation = useCallback(() => {
        if (![ORIENTATION_STATUSES.INVITED, ORIENTATION_STATUSES.RUNNING, ORIENTATION_STATUSES.WAITING, ORIENTATION_STATUSES.PAUSED].includes(state.status)) return;
        persist(ORIENTATION_STATUSES.DISMISSED, currentStep.id, null);
        dispatch({ type: 'DISMISS' });
        markTutorialCompletedForAccount();
    }, [currentStep.id, markTutorialCompletedForAccount, persist, state.status]);

    const postponeOrientation = useCallback(() => {
        memoryInvitationSuppressedRef.current = true;
        suppressInvitation({ sessionStorage: sessionStorageObject, userId });
        dispatch({ type: 'HIDE_INVITATION' });
    }, [sessionStorageObject, userId]);

    const goToStep = useCallback((stepIndex) => {
        const nextStep = ORIENTATION_TOUR[stepIndex];
        if (!nextStep || state.status !== ORIENTATION_STATUSES.RUNNING) return;

        moveToRouteForStep(nextStep);
        persist(ORIENTATION_STATUSES.PAUSED, nextStep.id, null);
        dispatch({ type: 'SET_STEP', payload: { stepIndex } });
    }, [moveToRouteForStep, persist, state.status]);

    const nextOrientationStep = useCallback(() => {
        if (state.currentStepIndex === ORIENTATION_TOUR.length - 1) {
            persist(ORIENTATION_STATUSES.COMPLETED, 'finish', null);
            dispatch({ type: 'COMPLETE' });
            markTutorialCompletedForAccount();
            return;
        }
        goToStep(state.currentStepIndex + 1);
    }, [goToStep, markTutorialCompletedForAccount, persist, state.currentStepIndex]);

    const previousOrientationStep = useCallback(() => {
        if (state.currentStepIndex === 0) return;
        goToStep(state.currentStepIndex - 1);
    }, [goToStep, state.currentStepIndex]);

    useEffect(() => {
        if (![ORIENTATION_STATUSES.RUNNING, ORIENTATION_STATUSES.WAITING].includes(state.status)) return;

        if (currentStep.routes.includes(location.pathname)) {
            if (expectedRouteRef.current === location.pathname) expectedRouteRef.current = null;
            return;
        }

        if (expectedRouteRef.current === currentStep.preferredRoute) return;
        if (state.status === ORIENTATION_STATUSES.WAITING) {
            pauseWaitingOrientation();
        } else {
            pauseOrientation();
        }
    }, [currentStep, location.pathname, pauseOrientation, pauseWaitingOrientation, state.status]);

    useEffect(() => {
        if (state.status === ORIENTATION_STATUSES.RUNNING && blockingModal) {
            pauseOrientation();
        }
    }, [blockingModal, pauseOrientation, state.status]);

    useEffect(() => {
        if (state.status !== ORIENTATION_STATUSES.RUNNING || typeof MutationObserver === 'undefined') return undefined;

        const observer = new MutationObserver(() => {
            if (hasBlockingDocumentDialog()) pauseOrientation();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [pauseOrientation, state.status]);

    useEffect(() => {
        if (
            state.status !== ORIENTATION_STATUSES.WAITING
            || state.waiting?.resumeMode !== 'automatic'
            || !state.waiting.checkpointId
            || !currentStep.routes.includes(location.pathname)
        ) return undefined;

        let cancelled = false;
        const restoreTargets = state.waiting.isRestoring
            ? state.waiting.restoreTargets || []
            : [];
        const watcher = targetResolver({
            targetCandidates: [
                state.waiting.checkpointId,
                ...restoreTargets.map((target) => target.checkpointId),
            ],
            timeoutMs: null,
            isReady: () => !blockingModal && !hasBlockingDocumentDialog(),
        });
        watcher.promise.then((target) => {
            if (cancelled || !target) return;
            const resolvedCheckpointId = target.getAttribute?.(HELP_ANCHOR_ATTRIBUTE);
            const restoreTarget = restoreTargets.find(
                (candidate) => candidate.checkpointId === resolvedCheckpointId
            );
            if (restoreTarget) {
                const stepIndex = getOrientationStepIndex(restoreTarget.stepId);
                persist(ORIENTATION_STATUSES.PAUSED, restoreTarget.stepId, null);
                dispatch({ type: 'START', payload: { stepIndex } });
                return;
            }
            const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            target.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
            persist(ORIENTATION_STATUSES.PAUSED, currentStep.id, null);
            dispatch({ type: 'CONTINUE_WAITING' });
        });

        return () => {
            cancelled = true;
            watcher.cancel();
        };
    }, [
        blockingModal,
        currentStep,
        location.pathname,
        persist,
        state.status,
        state.waiting,
        targetResolver,
    ]);

    useEffect(() => {
        if (
            state.status !== ORIENTATION_STATUSES.RUNNING
            || !currentStep.routes.includes(location.pathname)
        ) return undefined;

        if (currentStep.targetCandidates.length === 0) {
            dispatch({ type: 'TARGET_MISSING' });
            return undefined;
        }

        let cancelled = false;
        dispatch({ type: 'TARGET_RESOLVING' });
        const watcher = targetResolver({ targetCandidates: currentStep.targetCandidates });
        watcher.promise.then((target) => {
            if (cancelled) return;

            if (target) {
                dispatch({ type: 'TARGET_RESOLVED', payload: { target } });
                const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
                target.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
                return;
            }

            if (currentStep.missingTargetStepId) {
                goToStep(getOrientationStepIndex(currentStep.missingTargetStepId));
            } else if (currentStep.missingTarget === 'pause') {
                pauseOrientation();
            } else if (currentStep.missingTarget === 'skip') {
                nextOrientationStep();
            } else {
                dispatch({ type: 'TARGET_MISSING' });
            }
        });

        return () => {
            cancelled = true;
            watcher.cancel();
        };
    }, [
        currentStep,
        goToStep,
        location.pathname,
        nextOrientationStep,
        pauseOrientation,
        state.status,
        targetResolver,
    ]);

    const contextValue = useMemo(() => ({
        canUseOrientation,
        orientationStatus: state.status,
        invitationKind: state.invitationKind,
        currentStep,
        currentStepIndex: state.currentStepIndex,
        totalSteps: ORIENTATION_TOUR.length,
        target: state.target,
        targetState: state.targetState,
        waiting: state.waiting,
        startOrientation,
        resumeOrientation,
        restartOrientation,
        pauseOrientation,
        dismissOrientation,
        postponeOrientation,
        nextOrientationStep,
        previousOrientationStep,
        beginOrientationInteraction,
        continueWaitingOrientation,
        pauseWaitingOrientation,
    }), [
        canUseOrientation,
        beginOrientationInteraction,
        continueWaitingOrientation,
        currentStep,
        dismissOrientation,
        nextOrientationStep,
        pauseOrientation,
        pauseWaitingOrientation,
        postponeOrientation,
        previousOrientationStep,
        restartOrientation,
        resumeOrientation,
        startOrientation,
        state.currentStepIndex,
        state.invitationKind,
        state.status,
        state.target,
        state.targetState,
        state.waiting,
    ]);

    return (
        <HelpContext.Provider value={contextValue}>
            {children}
            {canUseOrientation
                && state.status === ORIENTATION_STATUSES.INVITED
                && INVITATION_ROUTES.includes(location.pathname)
                && !blockingModal
                && (
                    <HelpInvitation
                        kind={state.invitationKind}
                        onStart={startOrientation}
                        onResume={resumeOrientation}
                        onPostpone={postponeOrientation}
                        onDismiss={dismissOrientation}
                    />
                )}
            {canUseOrientation && state.status === ORIENTATION_STATUSES.RUNNING && (
                <HelpTour returnFocusElement={returnFocusRef.current} />
            )}
            {canUseOrientation
                && state.status === ORIENTATION_STATUSES.WAITING
                && currentStep.routes.includes(location.pathname)
                && !blockingModal
                && (
                    <HelpWaitingPrompt />
                )}
        </HelpContext.Provider>
    );
};

export default HelpProvider;
